import React, { forwardRef } from 'react';
import { cn } from '../../../utils/cn';

/**
 * GlassRadio - Executive design system radio button component
 *
 * A premium radio button with glass morphism effects and smooth animations.
 *
 * @example
 * ```tsx
 * <GlassRadio
 *   checked={selectedValue === 'option1'}
 *   onChange={() => setSelectedValue('option1')}
 *   label="Option 1"
 *   name="options"
 *   value="option1"
 * />
 * ```
 */

export interface GlassRadioProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'type' | 'size'> {
  /** Current checked state */
  checked: boolean;
  /** Callback when selected */
  onChange: (value: string) => void;
  /** Radio value */
  value: string;
  /** Radio group name */
  name: string;
  /** Optional label text */
  label?: string;
  /** Optional description text */
  description?: string;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Error state */
  error?: boolean;
  /** Additional CSS classes */
  className?: string;
}

const sizeClasses = {
  sm: {
    box: 'w-4 h-4',
    dot: 'w-1.5 h-1.5',
    label: 'text-sm',
    description: 'text-xs',
  },
  md: {
    box: 'w-5 h-5',
    dot: 'w-2 h-2',
    label: 'text-sm',
    description: 'text-xs',
  },
  lg: {
    box: 'w-6 h-6',
    dot: 'w-2.5 h-2.5',
    label: 'text-base',
    description: 'text-sm',
  },
};

export const GlassRadio = forwardRef<HTMLInputElement, GlassRadioProps>(
  (
    {
      checked,
      onChange,
      value,
      name,
      label,
      description,
      size = 'md',
      error = false,
      className,
      disabled,
      id,
      ...props
    },
    ref
  ) => {
    const radioId = id || `glass-radio-${Math.random().toString(36).substr(2, 9)}`;
    const sizes = sizeClasses[size];

    const handleChange = () => {
      onChange(value);
    };

    return (
      <div className={cn('relative', className)}>
        <label
          htmlFor={radioId}
          className={cn(
            'flex items-start gap-3 cursor-pointer group',
            disabled && 'cursor-not-allowed opacity-50'
          )}
        >
          {/* Hidden native radio */}
          <input
            ref={ref}
            type="radio"
            id={radioId}
            name={name}
            value={value}
            checked={checked}
            onChange={handleChange}
            disabled={disabled}
            className="sr-only peer"
            {...props}
          />

          {/* Custom radio circle */}
          <span
            className={cn(
              sizes.box,
              'relative flex-shrink-0 rounded-full',
              'border-2 transition-all duration-200',
              // Unchecked state
              !checked && [
                'bg-white/80 dark:bg-slate-800/80',
                'border-slate-300 dark:border-slate-600',
                'group-hover:border-[var(--accent-blue)] group-hover:bg-white dark:group-hover:bg-bg-tertiary',
              ],
              // Checked state
              checked && [
                'bg-white dark:bg-slate-900',
                'border-[var(--accent-blue)]',
                'shadow-[0_0_0_2px_rgba(59,130,246,0.15)]',
              ],
              // Error state
              error && !checked && 'border-rose-500 group-hover:border-rose-600',
              // Focus state (via peer)
              'peer-focus-visible:ring-2 peer-focus-visible:ring-[var(--accent-blue)]/40 peer-focus-visible:ring-offset-2'
            )}
          >
            {/* Inner dot */}
            {checked && (
              <span
                className={cn(
                  sizes.dot,
                  'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
                  'rounded-full',
                  'bg-gradient-to-br from-blue-500 to-blue-600',
                  'shadow-[var(--shadow-glow-blue)]',
                  'animate-in zoom-in-50 duration-150'
                )}
              />
            )}
          </span>

          {/* Label and description */}
          {(label || description) && (
            <div className="flex flex-col">
              {label && (
                <span
                  className={cn(
                    sizes.label,
                    'font-medium text-foreground',
                    'select-none'
                  )}
                >
                  {label}
                </span>
              )}
              {description && (
                <span
                  className={cn(
                    sizes.description,
                    'text-muted-foreground/60 mt-0.5'
                  )}
                >
                  {description}
                </span>
              )}
            </div>
          )}
        </label>
      </div>
    );
  }
);

GlassRadio.displayName = 'GlassRadio';

/**
 * GlassRadioGroup - Container for radio buttons
 */
export interface GlassRadioGroupProps {
  /** Current selected value */
  value: string;
  /** Callback when selection changes */
  onChange: (value: string) => void;
  /** Radio group name */
  name: string;
  /** Radio options */
  options: Array<{
    value: string;
    label: string;
    description?: string;
    disabled?: boolean;
  }>;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Layout direction */
  direction?: 'horizontal' | 'vertical';
  /** Error state */
  error?: boolean;
  /** Error message */
  errorMessage?: string;
  /** Additional CSS classes */
  className?: string;
}

export const GlassRadioGroup: React.FC<GlassRadioGroupProps> = ({
  value,
  onChange,
  name,
  options,
  size = 'md',
  direction = 'vertical',
  error = false,
  errorMessage,
  className,
}) => {
  return (
    <div className={className}>
      <div
        className={cn(
          'flex gap-3',
          direction === 'vertical' ? 'flex-col' : 'flex-row flex-wrap'
        )}
      >
        {options.map((option) => (
          <GlassRadio
            key={option.value}
            name={name}
            value={option.value}
            checked={value === option.value}
            onChange={onChange}
            label={option.label}
            description={option.description}
            size={size}
            disabled={option.disabled}
            error={error}
          />
        ))}
      </div>

      {/* Error message */}
      {error && errorMessage && (
        <p className="mt-2 text-sm text-rose-500">{errorMessage}</p>
      )}
    </div>
  );
};

GlassRadioGroup.displayName = 'GlassRadioGroup';
